import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, ShieldCheck, Lock, ArrowRight, X, Sparkles, Globe2, Send, CheckCircle2, Clock, LogOut, LayoutDashboard, MessageSquare } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import MemberCard, { Avatar } from '../components/community/MemberCard';
import InboxFab from '../components/community/InboxFab';
import { useAuth } from '../context/AuthContext';
import { COMMUNITY_MEMBERS, COMMUNITY_STATS } from '../data/communityMembers';

const API = process.env.REACT_APP_BACKEND_URL;

const CROCommunityPage = () => {
  const { user, isMember, isAdmin, login, logout } = useAuth();
  const navigate = useNavigate();
  const [applyOpen, setApplyOpen] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!isMember) return;
    (async () => {
      try {
        const [d, c] = await Promise.all([
          fetch(`${API}/api/community/directory`, { credentials: 'include' }),
          fetch(`${API}/api/community/conversations`, { credentials: 'include' }),
        ]);
        if (d.ok) setDirectory((await d.json()).members || []);
        if (c.ok) setUnread((await c.json()).unread || 0);
      } catch (_) { /* ignore */ }
    })();
  }, [isMember]);

  const status = user?.status || 'none';
  const teaserCount = 9;
  const visible = isMember ? COMMUNITY_MEMBERS : COMMUNITY_MEMBERS.slice(0, teaserCount);
  const hiddenCount = COMMUNITY_STATS.members - visible.length;

  const spotlight = COMMUNITY_MEMBERS[0];
  const PALETTE = ['#4f46e5', '#0d9488', '#b45309', '#be123c', '#7c3aed', '#0369a1'];
  const decorate = (m) => {
    const parts = (m.name || '?').split(' ').filter(Boolean);
    const initials = ((parts[0]?.[0] || '?') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
    let h = 0; for (const c of (m.user_id || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return { ...m, initials, color: PALETTE[h % PALETTE.length] };
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="The Revenue Leaders Community"
        description="A private, vetted community of Chief Revenue Officers and revenue leaders. Apply, get approved, and connect with the people who run revenue at the best companies."
        path="/community"
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="pt-24 pb-16 border-b border-slate-100 bg-gradient-to-b from-slate-900 to-slate-800 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '28px 28px' }} />
          <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-300 mb-5">
              <Users className="w-4 h-4" /> The Revenue Leaders Community
            </div>
            <h1 className="text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.03] max-w-3xl">
              A private room for the people who own the number.
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">
              A vetted network of Chief Revenue Officers and revenue leaders — the plays that work, the plugins they run, and warm intros to peers who have solved what you are facing. Membership is by application and reviewed one by one.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {!isMember && (
                <button
                  data-testid="community-apply-hero"
                  onClick={() => (user ? navigate('/community/apply') : setApplyOpen(true))}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors"
                >
                  {user ? 'Complete your application' : 'Apply to join'} <ArrowRight className="w-4 h-4" />
                </button>
              )}
              {isMember && (
                <>
                  <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-sm font-semibold">
                    <CheckCircle2 className="w-4 h-4" /> You are a vetted member
                  </span>
                  <button onClick={() => navigate('/community/inbox')} data-testid="community-messages-btn" className="relative inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-500 text-white text-base font-bold hover:bg-emerald-400 shadow-lg shadow-emerald-500/25 hover:-translate-y-0.5 transition-all">
                    <MessageSquare className="w-5 h-5" /> Open your inbox
                    {unread > 0 && <span className="absolute -top-2 -right-2 text-[11px] font-extrabold text-white bg-red-500 rounded-full px-2 py-0.5 min-w-5 text-center ring-2 ring-slate-900">{unread}</span>}
                  </button>
                </>
              )}
              {isAdmin && (
                <Link to="/admin/community" data-testid="community-admin-link" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors">
                  <LayoutDashboard className="w-4 h-4" /> Admin review
                </Link>
              )}
              {user && (
                <button onClick={logout} data-testid="community-logout" className="inline-flex items-center gap-2 px-4 py-3 text-slate-300 text-sm hover:text-white transition-colors">
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              )}
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
              <Stat value={COMMUNITY_STATS.members} label="Vetted members" />
              <Stat value={COMMUNITY_STATS.waitlist} label="On the waitlist" />
              <Stat value={COMMUNITY_STATS.applied_this_week} label="Applied this week" />
              <Stat value={COMMUNITY_STATS.countries} label="Countries" />
            </div>
          </div>
        </section>

        {/* Status banner for logged-in applicants */}
        {user && !isMember && (
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div data-testid="community-status-banner" className={`mt-6 rounded-xl border px-5 py-4 flex items-center gap-3 ${status === 'pending' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
              {status === 'pending' ? <Clock className="w-5 h-5 text-amber-600" /> : <Sparkles className="w-5 h-5 text-slate-500" />}
              <div className="text-sm">
                {status === 'pending' ? (
                  <><span className="font-semibold text-slate-900">Application under review.</span> <span className="text-slate-600">We review every applicant by hand — you will get an email when you are approved.</span></>
                ) : status === 'rejected' ? (
                  <><span className="font-semibold text-slate-900">Not approved this round.</span> <span className="text-slate-600">You can strengthen and resubmit your application.</span></>
                ) : (
                  <><span className="font-semibold text-slate-900">You are signed in.</span> <span className="text-slate-600">Complete your application to be considered for membership.</span></>
                )}
              </div>
              {status !== 'pending' && (
                <button onClick={() => navigate('/community/apply')} className="ml-auto text-sm font-semibold text-slate-900 hover:underline">Continue →</button>
              )}
            </div>
          </div>
        )}

        {/* How it works */}
        <section className="py-14 border-b border-slate-100">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Step icon={Send} n="01" title="Apply" body="Sign in and tell us your role, company, and what you want from the room. Two minutes." />
            <Step icon={ShieldCheck} n="02" title="Get vetted" body="A human reviews every application. We keep it to genuine revenue leaders — no vendors, no noise." />
            <Step icon={Users} n="03" title="Join & connect" body="Once approved, unlock every profile, message peers directly, and see the plugins they run." />
          </div>
        </section>

        {/* Member directory (real, chat-enabled) */}
        {isMember && (
          <section className="pt-14 border-b border-slate-100">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
              <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-600 mb-2">Members online</div>
                  <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">People you can message right now</h2>
                  <p className="text-slate-600 mt-1 text-sm">Vetted members who have joined. Start a real conversation.</p>
                </div>
                <button onClick={() => navigate('/community/inbox')} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
                  <MessageSquare className="w-4 h-4" /> Open inbox
                </button>
              </div>
              {directory.length === 0 ? (
                <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-6 text-center" data-testid="community-directory-empty">
                  You are one of the first here. As more members join, they will appear for you to message.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-directory">
                  {directory.map((m) => {
                    const dm = decorate(m);
                    return (
                      <button key={m.user_id} onClick={() => navigate(`/community/inbox?to=${m.user_id}`)} data-testid={`directory-member-${m.user_id}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 hover:shadow-md transition-all text-left">
                        {m.picture ? <img src={m.picture} alt="" className="w-11 h-11 rounded-full object-cover" /> : <Avatar member={dm} size={44} />}
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 text-sm truncate">{m.name}</div>
                          <div className="text-xs text-slate-500 truncate">{[m.title, m.company].filter(Boolean).join(' · ') || 'Member'}</div>
                        </div>
                        <MessageSquare className="w-4 h-4 text-slate-400" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Member directory */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Inside the room</div>
                <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">{COMMUNITY_STATS.members} vetted revenue leaders</h2>
                <p className="text-slate-600 mt-1">{isMember ? 'Message anyone directly.' : 'A glimpse of who is inside. Messaging unlocks once you are approved.'}</p>
              </div>
              {!isMember && (
                <button onClick={() => (user ? navigate('/community/apply') : setApplyOpen(true))} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
                  <Lock className="w-4 h-4" /> Apply to unlock all
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((m) => (
                <MemberCard key={m.id} member={m} unlocked={isMember} />
              ))}
            </div>

            {/* Locked "more members" panel */}
            {!isMember && hiddenCount > 0 && (
              <div data-testid="community-unlock-panel" className="relative mt-6 rounded-2xl border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-3 md:grid-cols-6 gap-4 p-6 blur-[3px] select-none pointer-events-none" aria-hidden="true">
                  {COMMUNITY_MEMBERS.slice(0, 6).map((m) => (
                    <div key={m.id} className="flex flex-col items-center gap-2">
                      <Avatar member={m} size={44} />
                      <div className="h-2 w-16 bg-slate-200 rounded" />
                      <div className="h-2 w-10 bg-slate-100 rounded" />
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center text-center px-6">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-900 text-white mb-3"><Lock className="w-5 h-5" /></span>
                  <div className="text-xl font-bold text-slate-900">{hiddenCount}+ more vetted leaders inside</div>
                  <p className="text-sm text-slate-600 mt-1 max-w-md">See every profile, their tool stack, and message them directly — once your application is approved.</p>
                  <button onClick={() => (user ? navigate('/community/apply') : setApplyOpen(true))} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
                    {user ? 'Complete your application' : 'Apply to join'} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Spotlight + why join */}
        <section className="py-16 bg-slate-50 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">Member spotlight</div>
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <Avatar member={spotlight} size={60} />
                  <div>
                    <div className="font-bold text-slate-900 text-lg">{spotlight.name}</div>
                    <div className="text-sm text-slate-600">{spotlight.title}</div>
                    <div className="text-sm font-semibold text-slate-900">{spotlight.company}</div>
                  </div>
                </div>
                {spotlight.bio && <p className="text-sm text-slate-600 leading-relaxed mt-4 line-clamp-4">{spotlight.bio}</p>}
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">Why leaders join</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Perk icon={Users} title="Peer intros, not cold outreach" body="Warm introductions to CROs and VPs who have solved your exact problem." />
                <Perk icon={Sparkles} title="Plays that actually worked" body="Real playbooks and post-mortems shared member-to-member." />
                <Perk icon={LayoutDashboard} title="The stack, exposed" body="See which plugins and tools peers run — and what they quietly dropped." />
                <Perk icon={Globe2} title="Global, but tight" body="Leaders across 14 countries. Small enough that everyone is real." />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Apply modal */}
      {applyOpen && (
        <Modal onClose={() => setApplyOpen(false)} testid="community-apply-modal">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Members only</div>
          <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">This unlocks once you are vetted</h2>
          <p className="text-sm text-slate-600 leading-relaxed mb-5">
            Messaging and full profiles are reserved for approved members. It keeps the room genuine — only real revenue leaders, no vendors.
          </p>
          {!user ? (
            <button onClick={login} data-testid="community-google-login" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
              <GoogleGlyph /> Continue with Google to apply
            </button>
          ) : status === 'pending' ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 flex items-center gap-2"><Clock className="w-4 h-4" /> Your application is under review.</div>
          ) : (
            <button onClick={() => { setApplyOpen(false); navigate('/community/apply'); }} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
              Complete your application <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </Modal>
      )}

      <InboxFab />
    </div>
  );
};

const Stat = ({ value, label }) => (
  <div><div className="text-3xl font-extrabold tracking-tight">{value}</div><div className="text-xs font-medium uppercase tracking-wider text-slate-400 mt-1">{label}</div></div>
);
const Step = ({ icon: Icon, n, title, body }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6">
    <div className="flex items-center gap-3 mb-3"><span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900 text-white"><Icon className="w-5 h-5" /></span><span className="text-xs font-mono text-slate-400">{n}</span></div>
    <div className="text-lg font-bold text-slate-900">{title}</div>
    <p className="text-sm text-slate-600 leading-relaxed mt-1">{body}</p>
  </div>
);
const Perk = ({ icon: Icon, title, body }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <Icon className="w-5 h-5 text-slate-900 mb-2" />
    <div className="font-semibold text-slate-900 text-sm">{title}</div>
    <p className="text-sm text-slate-600 leading-relaxed mt-1">{body}</p>
  </div>
);
const Modal = ({ children, onClose, testid }) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onClose} data-testid={testid}>
    <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-7" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-900" aria-label="Close"><X className="w-5 h-5" /></button>
      {children}
    </div>
  </div>
);
const GoogleGlyph = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true"><path fill="#fff" d="M12 11v2.8h4a3.9 3.9 0 0 1-1.7 2.5v2h2.7A8 8 0 0 0 20 12c0-.6-.1-1.3-.2-1.9H12z" opacity=".0"/><path fill="currentColor" d="M12 5c1.6 0 2.7.7 3.3 1.2l2.3-2.2C16.2 2.7 14.3 2 12 2 8.5 2 5.5 4 4 6.9l2.7 2.1C7.4 6.9 9.5 5 12 5z"/><path fill="currentColor" d="M12 22c2.3 0 4.2-.8 5.6-2.1l-2.7-2c-.7.5-1.7.9-2.9.9-2.5 0-4.6-1.7-5.3-4L4 16.9C5.5 19.9 8.5 22 12 22z" opacity=".85"/><path fill="currentColor" d="M20 12c0-.6-.1-1.3-.2-1.9H12V13h4.4a3.9 3.9 0 0 1-1.6 2.6l2.7 2A8 8 0 0 0 20 12z" opacity=".7"/></svg>
);

export default CROCommunityPage;
